'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import {
  ShieldCheck,
  GraduationCap,
  FileText,
  Download,
  Mail,
  Lock,
  Users,
  CheckCircle2,
  ArrowRight,
  Menu,
  X,
  Phone,
  Star,
  Award,
  BookOpen,
  MapPin,
  Globe,
  ClipboardCheck,
} from 'lucide-react';

const programmes = [
  'Strategic Management and Leadership Development Level 6',
  'Human Resource Management Level 5',
  'Human Resource Management Level 4',
  'Logistics Management Level 5',
  'Business Administration Level 5',
  'Business Administration Management Level 3',
  'Supervisory Management Level 3',
  'Small Business Management Level 4',
  'Training and Development Level 4',
  'TVET Instruction/Delivery Level 5',
  'Assessment Level 4',
  'Digital Marketing Management Level 4',
  'Early Childhood Development Level 4',
  'Construction Project Management Level 5',
];

export default function LandingPageClient() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* ── NAVBAR ── */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm border-b border-border' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 border border-white/20">
                <Image
                  src="/assets/images/logo_1_1_-1778028950193.jpeg"
                  alt="Ziva Niara Solutions logo"
                  width={40}
                  height={40}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <span className="block text-sm font-bold text-primary leading-tight">Ziva DocuSecure</span>
                <span className="block text-[10px] text-muted-foreground leading-tight">Ziva Niara Solutions</span>
              </div>
            </div>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-6">
              <a href="#home" className="text-sm font-medium text-foreground hover:text-primary transition-colors">Home</a>
              <a href="#features" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">Features</a>
              <a href="#programmes" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">Programmes</a>
              <a href="#contact" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">Contact</a>
            </nav>

            {/* Desktop CTA */}
            <div className="hidden md:flex items-center gap-2">
              <Link href="/student-sign-in" className="btn-outline text-xs px-3 py-1.5">Student Sign In</Link>
              <Link href="/assessment-sign-in" className="btn-outline text-xs px-3 py-1.5 text-emerald-700 border-emerald-300 hover:bg-emerald-50">Assessment</Link>
              <Link href="/manager-sign-in" className="btn-outline text-xs px-3 py-1.5 text-indigo-700 border-indigo-300 hover:bg-indigo-50">Manager</Link>
              <Link href="/admin-sign-in" className="btn-primary text-xs px-3 py-1.5">Admin Sign In</Link>
            </div>

            {/* Mobile menu toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg border border-border text-foreground"
            >
              {mobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-border px-4 py-4 space-y-3 animate-fade-in">
            <a href="#home" className="block text-sm font-medium text-foreground py-2" onClick={() => setMobileMenuOpen(false)}>Home</a>
            <a href="#features" className="block text-sm font-medium text-muted-foreground py-2" onClick={() => setMobileMenuOpen(false)}>Features</a>
            <a href="#programmes" className="block text-sm font-medium text-muted-foreground py-2" onClick={() => setMobileMenuOpen(false)}>Programmes</a>
            <a href="#contact" className="block text-sm font-medium text-muted-foreground py-2" onClick={() => setMobileMenuOpen(false)}>Contact</a>
            <div className="pt-2 space-y-2 border-t border-border">
              <Link href="/student-sign-in" className="btn-outline w-full text-sm">Student Sign In</Link>
              <Link href="/student-register" className="btn-secondary w-full text-sm">Student Registration</Link>
              <Link href="/assessment-sign-in" className="btn-outline w-full text-sm">Assessment Manager</Link>
              <Link href="/manager-sign-in" className="btn-outline w-full text-sm text-indigo-700 border-indigo-300">Manager Sign In</Link>
              <Link href="/admin-sign-in" className="btn-primary w-full text-sm">Admin Sign In</Link>
            </div>
          </div>
        )}
      </header>
      {/* ── HERO ── */}
      <section id="home" className="relative pt-16 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-purple-800 to-purple-950" />
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-white/5" />
          <div className="absolute top-1/2 -left-24 w-64 h-64 rounded-full bg-accent/10" />
          <div className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full bg-white/5" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left content */}
            <div>
              {/* Badge */}
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-6">
                <ShieldCheck size={13} className="text-accent" />
                <span className="text-xs font-semibold text-white/90">Secure Document Portal</span>
              </div>

              <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight text-balance mb-6">
                Ziva{' '}
                <span className="text-accent">DocuSecure</span>
              </h1>
              <p className="text-lg text-white/75 leading-relaxed mb-4 max-w-lg">
                The official secure student registration and document submission portal for{' '}
                <strong className="text-white">Ziva Niara Solutions</strong>.
              </p>
              <p className="text-sm text-white/60 mb-8 max-w-md">
                Complete your biodata form, upload required documents, track your submission status, and receive programme confirmation — all in one place.
              </p>

              {/* Primary action buttons */}
              <div className="flex flex-wrap gap-3 mb-6">
                <Link
                  href="/student-sign-in"
                  className="inline-flex items-center gap-2 bg-white text-primary font-semibold text-sm px-5 py-3 rounded-xl hover:bg-white/90 transition-all active:scale-95 shadow-lg"
                >
                  <GraduationCap size={16} />
                  Student Sign In
                </Link>
                <Link
                  href="/student-register"
                  className="inline-flex items-center gap-2 bg-accent text-white font-semibold text-sm px-5 py-3 rounded-xl hover:bg-amber-600 transition-all active:scale-95 shadow-lg"
                >
                  <Users size={16} />
                  Student Registration
                </Link>
              </div>

              {/* Secondary buttons */}
              <div className="flex flex-wrap gap-3">
                <Link
                  href="/manager-sign-in"
                  className="inline-flex items-center gap-2 bg-indigo-600/80 border border-indigo-400/50 text-white font-medium text-sm px-4 py-2.5 rounded-xl hover:bg-indigo-600 transition-all"
                >
                  <ClipboardCheck size={14} />
                  Manager Sign In
                </Link>
                <Link
                  href="/admin-sign-in"
                  className="inline-flex items-center gap-2 bg-white/10 border border-white/25 text-white font-medium text-sm px-4 py-2.5 rounded-xl hover:bg-white/20 transition-all"
                >
                  <Lock size={14} />
                  Admin Sign In
                </Link>
                <Link
                  href="/download-form"
                  className="inline-flex items-center gap-2 bg-white/10 border border-white/25 text-white font-medium text-sm px-4 py-2.5 rounded-xl hover:bg-white/20 transition-all"
                >
                  <Download size={14} />
                  Download Registration Form
                </Link>
                <a
                  href="#contact"
                  className="inline-flex items-center gap-2 bg-white/10 border border-white/25 text-white font-medium text-sm px-4 py-2.5 rounded-xl hover:bg-white/20 transition-all"
                >
                  <Mail size={14} />
                  Contact Support
                </a>
              </div>
            </div>

            {/* Right card */}
            <div className="hidden lg:block">
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 space-y-4">
                {/* Logo area */}
                <div className="flex items-center gap-4 pb-4 border-b border-white/20">
                  <div className="w-16 h-16 rounded-xl overflow-hidden bg-white/20 flex-shrink-0 border border-white/30">
                    <Image
                      src="/assets/images/logo_1_1_-1778028950193.jpeg"
                      alt="Ziva Niara Solutions logo"
                      width={64}
                      height={64}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <p className="text-white font-bold text-base">Ziva Niara Solutions</p>
                    <p className="text-white/60 text-xs">docusecure.myzivasol.com</p>
                    <p className="text-white/50 text-[10px] mt-0.5">Suite 16, 4-5 Lismore Avenue, Kingston</p>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'Programmes', value: '14', icon: BookOpen },
                    { label: 'Secure Portal', value: '100%', icon: ShieldCheck },
                    { label: 'Document Types', value: '8', icon: FileText },
                    { label: 'Role-Based Access', value: '3 Roles', icon: Users },
                  ]?.map((stat) => {
                    const StatIcon = stat?.icon;
                    return (
                      <div key={stat?.label} className="bg-white/10 rounded-xl p-3">
                        <StatIcon size={16} className="text-accent mb-1.5" />
                        <p className="text-white font-bold text-lg leading-none">{stat?.value}</p>
                        <p className="text-white/60 text-xs mt-0.5">{stat?.label}</p>
                      </div>
                    );
                  })}
                </div>

                {/* Features list */}
                <div className="space-y-2 pt-2">
                  {[
                    'Electronic signature & declaration',
                    'Secure document upload & verification',
                    'Real-time submission status tracking',
                    'PDF form download & generation',
                  ]?.map((f) => (
                    <div key={f} className="flex items-center gap-2">
                      <CheckCircle2 size={13} className="text-accent flex-shrink-0" />
                      <span className="text-white/80 text-xs">{f}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Wave divider */}
        <div className="relative h-12 overflow-hidden">
          <svg viewBox="0 0 1440 48" fill="none" xmlns="http://www.w3.org/2000/svg" className="absolute bottom-0 w-full">
            <path d="M0 48L1440 48L1440 0C1200 40 960 48 720 48C480 48 240 40 0 0L0 48Z" fill="white" />
          </svg>
        </div>
      </section>
      {/* ── QUICK ACCESS CARDS ── */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-foreground">Access Your Portal</h2>
            <p className="text-muted-foreground text-sm mt-2">Choose your role to get started</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Student card */}
            <div className="group border border-border rounded-2xl p-6 hover:border-primary/40 hover:shadow-lg transition-all duration-200 bg-white">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                <GraduationCap size={22} className="text-primary" />
              </div>
              <h3 className="font-bold text-foreground text-base mb-2">Student Portal</h3>
              <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
                Access your biodata form, upload documents, track submission status, and download your completed registration form.
              </p>
              <div className="space-y-2">
                <Link href="/student-sign-in" className="btn-primary w-full text-sm flex items-center justify-center gap-2">
                  Student Sign In <ArrowRight size={14} />
                </Link>
                <Link href="/student-register" className="btn-outline w-full text-sm">
                  New Student Registration
                </Link>
              </div>
            </div>

            {/* Manager card */}
            <div className="group border border-indigo-200 rounded-2xl p-6 hover:border-indigo-400 hover:shadow-lg transition-all duration-200 bg-white">
              <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center mb-4 group-hover:bg-indigo-200 transition-colors">
                <ClipboardCheck size={22} className="text-indigo-700" />
              </div>
              <h3 className="font-bold text-foreground text-base mb-2">Manager Portal</h3>
              <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
                Review student submissions, verify uploaded documents, manage programme enrolments, and oversee assessment activities.
              </p>
              <div className="space-y-2">
                <Link href="/manager-sign-in" className="w-full text-sm flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-4 py-2.5 rounded-xl transition-all active:scale-95">
                  Manager Sign In <ArrowRight size={14} />
                </Link>
                <Link href="/assessment-sign-in" className="btn-outline w-full text-sm text-indigo-700 border-indigo-300 hover:bg-indigo-50">
                  Assessment Sign In
                </Link>
              </div>
            </div>

            {/* Admin card */}
            <div className="group border border-border rounded-2xl p-6 hover:border-primary/40 hover:shadow-lg transition-all duration-200 bg-white">
              <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-4 group-hover:bg-accent/20 transition-colors">
                <ShieldCheck size={22} className="text-accent" />
              </div>
              <h3 className="font-bold text-foreground text-base mb-2">Admin Portal</h3>
              <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
                Manage all student records, review documents, generate reports, and oversee programme registrations.
              </p>
              <div className="space-y-2">
                <Link href="/admin-sign-in" className="btn-primary w-full text-sm flex items-center justify-center gap-2">
                  Admin Sign In <ArrowRight size={14} />
                </Link>
                <Link href="/admin-dashboard" className="btn-outline w-full text-sm">
                  View Dashboard
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* ── FEATURES ── */}
      <section id="features" className="py-16 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-primary/10 rounded-full px-4 py-1.5 mb-4">
              <Star size={13} className="text-primary" />
              <span className="text-xs font-semibold text-primary">Platform Features</span>
            </div>
            <h2 className="text-3xl font-bold text-foreground">Everything You Need</h2>
            <p className="text-muted-foreground mt-3 max-w-xl mx-auto text-sm leading-relaxed">
              A complete document management system built specifically for Ziva Niara Solutions training programmes.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              {
                icon: FileText,
                title: 'Digital Biodata Form',
                desc: 'Complete your full registration form online with all required fields, programme selection, and employment details.',
                color: 'text-primary bg-primary/10',
              },
              {
                icon: ShieldCheck,
                title: 'Secure Document Upload',
                desc: 'Upload birth certificates, TRN, academic certificates, and other required documents — each with its own upload field.',
                color: 'text-accent bg-accent/10',
              },
              {
                icon: Award,
                title: 'E-Signature & Declaration',
                desc: 'Sign your declaration electronically before document upload is unlocked. Your signature is captured and stored securely.',
                color: 'text-purple-700 bg-purple-100',
              },
              {
                icon: CheckCircle2,
                title: 'Submission Status Tracking',
                desc: 'Track the status of your documents in real-time — from uploaded to verified or requiring resubmission.',
                color: 'text-green-700 bg-green-100',
              },
              {
                icon: Download,
                title: 'PDF Form Download',
                desc: 'Download a blank registration form or your completed signed form as a PDF at any time.',
                color: 'text-blue-700 bg-blue-100',
              },
              {
                icon: Lock,
                title: 'Role-Based Security',
                desc: 'Students, admins, and reviewers each have separate secure portals with appropriate access levels.',
                color: 'text-red-700 bg-red-100',
              },
            ]?.map((f) => {
              const FIcon = f?.icon;
              return (
                <div key={f?.title} className="bg-white rounded-2xl p-5 border border-border hover:shadow-md transition-shadow">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${f?.color}`}>
                    <FIcon size={18} />
                  </div>
                  <h3 className="font-bold text-foreground text-sm mb-2">{f?.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{f?.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      {/* ── STUDENT WORKFLOW ── */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-foreground">How It Works</h2>
            <p className="text-muted-foreground text-sm mt-2">Simple 6-step process for students</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { step: 1, title: 'Sign In or Register', desc: 'Create your account or sign in to your existing student portal.' },
              { step: 2, title: 'Complete Biodata Form', desc: 'Fill in your personal, programme, and employment information.' },
              { step: 3, title: 'Read & Accept Declaration', desc: 'Review the consent and declaration clauses and tick each one.' },
              { step: 4, title: 'Sign Electronically', desc: 'Draw your digital signature to confirm your declaration.' },
              { step: 5, title: 'Upload Documents', desc: 'Upload each required document using individual upload fields.' },
              { step: 6, title: 'Submit & Track', desc: 'Submit for review and track your verification status in real time.' },
            ]?.map((item) => (
              <div key={item?.step} className="flex items-start gap-3 p-4 border border-border rounded-xl hover:border-primary/30 hover:bg-secondary/20 transition-all">
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-white">{item?.step}</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">{item?.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{item?.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* ── PROGRAMMES ── */}
      <section id="programmes" className="py-16 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-foreground">Supported Programmes</h2>
            <p className="text-muted-foreground text-sm mt-2">14 accredited programmes available for registration</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {programmes?.map((prog) => (
              <div key={prog} className="flex items-start gap-2.5 p-3 border border-border rounded-xl hover:border-primary/30 hover:bg-white transition-all">
                <BookOpen size={14} className="text-primary flex-shrink-0 mt-0.5" />
                <span className="text-xs text-foreground leading-relaxed">{prog}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* ── DOWNLOAD FORM CTA ── */}
      <section className="py-14 bg-gradient-to-r from-primary to-purple-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Download size={32} className="text-accent mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-3">Download the Registration Form</h2>
          <p className="text-white/70 text-sm mb-6 max-w-lg mx-auto">
            Download the blank Ziva Niara Solutions student biodata and registration form as a PDF. Available for offline completion or reference.
          </p>
          <Link
            href="/download-form"
            className="inline-flex items-center gap-2 bg-accent text-white font-semibold px-6 py-3 rounded-xl hover:bg-amber-600 transition-all active:scale-95 shadow-lg"
          >
            <Download size={16} />
            Download Blank Registration Form
          </Link>
        </div>
      </section>
      {/* ── CONTACT ── */}
      <section id="contact" className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-foreground">Contact Support</h2>
            <p className="text-muted-foreground text-sm mt-2">Need help? Reach out to the Ziva Niara Solutions support team.</p>
          </div>

          {/* Contact cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[
              { icon: Phone, label: 'Main Phone', value: '876-277-3952', href: 'tel:+18762773952' },
              { icon: Mail, label: 'DocuSecure Support', value: 'docusecure@myzivasol.com', href: 'mailto:docusecure@myzivasol.com' },
              { icon: Globe, label: 'Website', value: 'www.thezivaniara.com', href: 'https://www.thezivaniara.com' },
            ]?.map((c) => {
              const CIcon = c?.icon;
              return (
                <a
                  key={c?.label}
                  href={c?.href}
                  className="flex flex-col items-center text-center p-5 border border-border rounded-2xl hover:border-primary/40 hover:shadow-md transition-all group"
                >
                  <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-3 group-hover:bg-primary/20 transition-colors">
                    <CIcon size={20} className="text-primary" />
                  </div>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">{c?.label}</p>
                  <p className="text-sm font-medium text-foreground break-all">{c?.value}</p>
                </a>
              );
            })}
          </div>

          {/* Address + Support phone */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="flex items-start gap-3 p-5 border border-border rounded-2xl">
              <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <MapPin size={20} className="text-primary" />
              </div>
              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-1">Office Address</p>
                <p className="text-sm font-medium text-foreground leading-relaxed">
                  Suite 16, 4-5 Lismore Avenue<br />
                  Kingston, St. Andrew, Jamaica
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-5 border border-border rounded-2xl">
              <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Phone size={20} className="text-primary" />
              </div>
              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-1">Contact Support</p>
                <a href="tel:+18768650444" className="text-sm font-medium text-primary hover:underline">1-876-865-0444</a>
                <p className="text-xs text-muted-foreground mt-1">For student registration support</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* ── FOOTER ── */}
      <footer className="bg-foreground text-white py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-start justify-between gap-8 mb-8">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl overflow-hidden bg-white/10 border border-white/20 flex-shrink-0">
                <Image
                  src="/assets/images/logo_1_1_-1778028950193.jpeg"
                  alt="Ziva Niara Solutions logo"
                  width={48}
                  height={48}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <p className="text-sm font-bold">Ziva DocuSecure</p>
                <p className="text-xs text-white/50">Ziva Niara Solutions</p>
                <p className="text-xs text-white/40 mt-0.5">docusecure.myzivasol.com</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-6 text-xs text-white/60">
              <div>
                <p className="font-semibold text-white/80 mb-2">Contact</p>
                <div className="space-y-1">
                  <p>876-277-3952</p>
                  <a href="mailto:docusecure@myzivasol.com" className="block hover:text-white transition-colors">docusecure@myzivasol.com</a>
                  <a href="https://www.thezivaniara.com" className="block hover:text-white transition-colors">www.thezivaniara.com</a>
                </div>
              </div>
              <div>
                <p className="font-semibold text-white/80 mb-2">Address</p>
                <p className="leading-relaxed">Suite 16, 4-5 Lismore Avenue<br />Kingston, St. Andrew, Jamaica</p>
                <p className="mt-2">Support: <a href="tel:+18768650444" className="hover:text-white transition-colors">1-876-865-0444</a></p>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-4 text-xs text-white/50">
              <Link href="/" className="hover:text-white transition-colors">Home</Link>
              <Link href="/student-sign-in" className="hover:text-white transition-colors">Student Sign In</Link>
              <Link href="/student-register" className="hover:text-white transition-colors">Student Registration</Link>
              <Link href="/download-form" className="hover:text-white transition-colors">Download Form</Link>
              <a href="#contact" className="hover:text-white transition-colors">Contact Support</a>
            </div>
            <p className="text-xs text-white/40">© 2026 Ziva Niara Solutions. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
